import { FormEvent, useEffect, useRef } from "react"
import { Head, Link, useForm, usePage } from "@inertiajs/react"
import { AuthShell } from "@/components/AuthShell"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

import type { PageProps } from "@/types/inertia"

type SignupProps = PageProps & { return_to?: string | null; honeypot_field: string; form_token: string }

export default function Signup() {
  const { props } = usePage<SignupProps>()
  const form = useForm({ name: "", email: "", password: "", timezone: "" })

  // Anti-bot, see RegistrationsController (#359). The honeypot must stay empty;
  // the signed form_token lets the server measure how long the form was open.
  const honeypotValue = useRef("")
  const honeypotField = props.honeypot_field
  const formToken = props.form_token

  useEffect(() => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (tz) form.setData("timezone", tz)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.transform((data) => ({
      ...data,
      form_token: formToken,
      [honeypotField]: honeypotValue.current,
      ...(props.return_to ? { return_to: props.return_to } : {}),
    }))
    form.post("/signup")
  }

  const errors = props.errors ?? {}

  return (
    <>
      <Head title="Sign up">
        <meta name="description" content="Create a free account to support the Australian Music Database." />
        <meta property="og:title" content="Sign up" />
        <meta property="og:description" content="Create a free account to support the Australian Music Database." />
      </Head>
      <AuthShell>
        <div className="text-center">
          <h2>Create your account</h2>
          <p className="mt-2 text-sm text-ink-muted">
            Already have an account?{" "}
            <Link href={props.return_to ? `/login?return_to=${encodeURIComponent(props.return_to)}` : "/login"}>
              Log in
            </Link>
          </p>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4">
          {/* Decoy field. Positioned off-screen rather than display:none, since
              some bots skip hidden inputs. Hidden from assistive tech and the
              tab order so it never reaches a real user. */}
          <div aria-hidden="true" className="absolute w-px h-px overflow-hidden -left-[9999px]">
            <label htmlFor={honeypotField}>Website</label>
            <input
              id={honeypotField}
              name={honeypotField}
              type="text"
              tabIndex={-1}
              autoComplete="off"
              defaultValue=""
              onChange={(e) => { honeypotValue.current = e.target.value }}
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="name">Name</label>
            <Input
              id="name"
              type="text"
              autoComplete="name"
              autoFocus
              required
              aria-invalid={!!errors.name}
              value={form.data.name}
              onChange={(e) => form.setData("name", e.target.value)}
            />
            {errors.name && (
              <p className="text-xs text-danger-display">{errors.name}</p>
            )}
          </div>
          <div className="space-y-2">
            <label htmlFor="email">Email</label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              aria-invalid={!!errors.email}
              value={form.data.email}
              onChange={(e) => form.setData("email", e.target.value)}
            />
            {errors.email && (
              <p className="text-xs text-danger-display">{errors.email}</p>
            )}
          </div>
          <div className="space-y-2">
            <label htmlFor="password">Password</label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={7}
              aria-invalid={!!errors.password}
              value={form.data.password}
              onChange={(e) => form.setData("password", e.target.value)}
            />
            {errors.password && (
              <p className="text-xs text-danger-display">{errors.password}</p>
            )}
            <p className="text-xs text-ink-muted">Minimum 7 characters. No other restrictions.</p>
          </div>
          <Button type="submit" disabled={form.processing} className="w-full">
            Create account
          </Button>
        </form>
      </AuthShell>
    </>
  )
}
